﻿import axios from 'axios';
import qs from 'query-string';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import isObject from 'lodash/isObject';
import localStorage from 'local-storage';
import * as Sentry from '@sentry/react';

import { logout } from 'pages/App/App.ducks';

import { apiBaseUrl } from './config';
import { defaultTenant, hasTenantHeader } from './tenant';

const axiosInstance = axios.create({
  baseURL: apiBaseUrl,
  paramsSerializer: (params) => qs.stringify(params),
});

const omitByDeeply = (object, predicate) => {
  Object.keys(object).forEach((key) => {
    if (isObject(object[key])) {
      omitByDeeply(object[key], predicate);
    }
    if (predicate(object[key])) {
      delete object[key];
    }
  });
  return object;
};

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.get('token');
  if (token) {
    config.headers['Content-Type'] = 'application/json';
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Never overwrite a tenant the request names itself; see config/tenant.jsx.
  if (!hasTenantHeader(config.headers)) {
    const tenant = defaultTenant({ signedIn: Boolean(token) });
    if (tenant) {
      config.headers.TenantId = tenant;
    }
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    const data = omitByDeeply(
      response.data,
      (value) => isUndefined(value) || isNull(value),
    );
    return {
      ...response,
      data,
    };
  },
  (err) => {
    // 401 only. A 404 used to end the session too, so any missing record -
    // a stale bookmark, a deleted client, a link shared after the fact - logged
    // the user out and lost whatever they had unsaved. The pages reachable with a
    // bad id all catch and toast already, so a 404 now surfaces as a message on
    // the page the user is actually on.
    const LOGOUT_STATUS = [401];

    // A rejected sign-in is not an expired session, and treating it as one is why a
    // wrong password showed the user nothing at all. login() catches the 401 and sets
    // "You have entered an invalid email/password combination", but the logout below
    // is dispatched from an async import(), so it landed *after* that, and
    // resetLogin() plus the root reducer's `state = undefined` wiped the message
    // before anyone could read it. There is no session to end here, so leave it be.
    // Note this matches the sign-in endpoint only: a 401 from token-auth/refresh/
    // really is an expired session and must still sign the user out.
    const isSignIn = /(^|\/)token-auth\/?$/.test(err.config?.url ?? '');

    if (LOGOUT_STATUS.includes(err.response?.status) && !isSignIn) {
      import('store')
        .then(({ store }) => {
          const { dispatch } = store;
          dispatch(logout());
        })
        .catch((e) => {
          Sentry.captureException(e);
        });
    }
    return Promise.reject(err);
  },
);

export default axiosInstance;
